<script setup lang="ts">
import type { Event } from '~/commerce/types/event';

const runtimeConfig = useRuntimeConfig();
const content_Url = runtimeConfig.public.karriere.content.url;

const premiumContent = {
    category: 'Arbeitsmarkt, Recruiting',
    title: 'Was Arbeitnehmer*innen im Berufsleben erreichen möchten',
    to: `${content_Url}/hr/t/karriereziele`,
};

const { data: events, status } = useLazyFetch<Event[]>('/api/commerce/content/events');

const cardLinks = [
    {
        class: 'bg-green-400',
        title: 'Recruiting',
        to: `${content_Url}/hr/t/recruiting`,
    },
    {
        class: 'bg-yellow-300',
        title: 'Arbeitsmarkt',
        to: `${content_Url}/hr/t/arbeitsmarkt`,
    },
    {
        class: 'bg-purple-300',
        title: 'Arbeitgebermarke',
        to: `${content_Url}/hr/t/arbeitgebermarke`,
    },
];
</script>

<template>
    <CommBaseContentContainer
        class="gap-dynamic grid grid-cols-1 py-2 lg:grid-cols-[5fr_4fr_3fr] lg:py-8"
    >
        <div class="flex flex-col gap-4">
            <JamBaseLink
                stroke-size="thin"
                :href="`${content_Url}/hr/tipps`"
                data-gtm-element="HEAD: Main Navigation"
                data-gtm-element-detail="Alle Downloads"
            >
                Alle Downloads
            </JamBaseLink>
            <div class="grid grid-cols-1 gap-2 lg:grid-cols-1 2xl:grid-cols-2">
                <NavigationPanelCardContent
                    v-bind="premiumContent"
                    classes="!flex-row"
                />
                <ul class="flex flex-col gap-2 text-lg font-bold">
                    <li
                        v-for="(link, idx) in cardLinks"
                        :key="`${link.title}-${idx}`"
                    >
                        <NavigationPanelCardContent
                            v-bind="link"
                            variant="slim"
                            :classes="link.class"
                        ></NavigationPanelCardContent>
                    </li>
                </ul>
            </div>
        </div>
        <div class="flex flex-col gap-4">
            <JamBaseLink
                stroke-size="thin"
                :href="`${content_Url}/hr/events`"
                data-gtm-element="HEAD: Main Navigation"
                data-gtm-element-detail="Alle Events"
                >Alle Events
            </JamBaseLink>
            <ul v-if="status === 'pending'" class="flex flex-col gap-4">
                <li
                    v-for="it in 3"
                    :key="it"
                    class="grid grid-cols-[120px_1fr] gap-3">
                    <JamBaseSkeletonLoader
                        height="h-full w-full mb-3 rounded"
                    />
                    <div class="flex flex-col gap-3">
                        <JamBaseSkeletonLoader
                            v-for="it in 3"
                            :key="it"
                            height="h-[20px] w-full rounded"
                        />
                    </div>
                </li>
            </ul>
            <ul
                v-else-if="events?.length"
                class="grid-cols-1 gap-4 md:grid-cols-2 lg:grid lg:grid-cols-1"
            >
                <li
                    v-for="(event, idx) in events"
                    :key="`${event.title}-${idx}`"
                >
                    <NuxtLink
                        class="hover-box-shadow-animation group mb-4 flex size-full flex-col justify-between gap-4 rounded-lg border border-gray-900 hover:border hover:border-gray-900 lg:mb-0 lg:flex-row lg:items-center lg:gap-6 lg:border-0 lg:bg-transparent"
                        external
                        :to="`${content_Url}${event.to}`"
                    >
                        <div
                            class="flex h-full flex-col items-start justify-between gap-3 p-4 xl:flex-row xl:gap-6"
                        >
                            <div class="flex justify-center gap-2">
                                <BaseHeading variant="2xl-500">
                                    {{ $dayjs(event.startDate).format('DD') }}
                                </BaseHeading>
                                <div class="self-center">
                                    <p class="uppercase">
                                        {{
                                            $dayjs(event.startDate).format(
                                                'MMM',
                                            )
                                        }}
                                    </p>
                                    <p>
                                        {{
                                            $dayjs(event.startDate).format('YY')
                                        }}
                                    </p>
                                </div>
                            </div>
                            <div
                                class="line-clamp-3 flex w-full items-end gap-4"
                            >
                                <BaseText
                                    variant="lg-500"
                                    class="line-clamp-3 hyphens-auto"
                                >
                                    {{ event.title }}
                                </BaseText>
                            </div>
                        </div>
                    </NuxtLink>
                </li>
            </ul>
            <div v-else class="flex flex-col gap-3">
                <img
                    src="/illustrations/basic-calendar.svg"
                    width="64"
                    height="64"
                    alt="Keine Events"
                />
                <div class="flex flex-col gap-6">
                    <div class="flex flex-col gap-2">
                        <BaseHeading variant="base-700">
                            Derzeit sind keine Events geplant.
                        </BaseHeading>
                        <BaseText>
                            Die Business Events von karriere.at bieten
                            kostenlose Einblicke in wichtige HR-Themen wie
                            Recruiting, Branding und Active Sourcing.
                        </BaseText>
                    </div>
                    <BaseText>
                        Werfen Sie einen Blick auf unsere
                        <JamBaseLink
                            variant="dark-gray"
                            :href="`${content_Url}/hr/events`"
                        >
                            vergangenen Events </JamBaseLink
                        >.
                    </BaseText>
                </div>
            </div>
        </div>
        <div class="flex flex-col gap-4">
            <JamBaseLink
                :href="`${content_Url}/c/p/podcast`"
                target="_blank"
                has-trailing-icon
                icon-name="extern-link"
                icon-size="medium"
                data-gtm-element="HEAD: Main Navigation"
                data-gtm-element-detail="Podcast anhören"
            >
                Podcast anhören
            </JamBaseLink>
            <div class="h-full gap-4 md:grid-cols-2 lg:grid lg:grid-cols-1">
                <NavigationPanelCardContent
                    title="Der Podcast zur Arbeitswelt in Österreich."
                    category="Zeitausgleich."
                    :to="`${content_Url}/c/p/podcast`"
                    target="_blank"
                    external-link
                    classes="md:flex-col !flex-row"
                />
            </div>
        </div>
    </CommBaseContentContainer>
    <NavigationPanelFooter class="hidden lg:block" />
</template>
